/**
 * When a text/note sits close enough to one object, treat that text as a
 * thought ABOUT the object — for AI scene context and the canvas indicator.
 *
 * Gap is the empty space between AABBs (0 when they overlap), not center distance.
 * Ambiguous midpoints (two objects similarly close) stay unbound.
 */

export const TEXT_ABOUT_MAX_GAP = 72;
export const TEXT_ABOUT_EXPLICIT_MAX_GAP = 160;
export const TEXT_ABOUT_AMBIGUITY_RATIO = 1.45;

export const TEXT_ABOUT_SOURCE_TYPES = new Set(['text', 'note']);
export const TEXT_ABOUT_TARGET_TYPES = new Set([
  'pin-card',
  'suggest-pin',
  'conversation-pin',
  'image',
  'bookmark',
  'embed',
  'geo',
  'video',
  'frame',
]);

const DEFAULT_SIZE = {
  'pin-card': { w: 300, h: 400 },
  'suggest-pin': { w: 300, h: 160 },
  'conversation-pin': { w: 300, h: 160 },
  note: { w: 200, h: 200 },
  text: { w: 160, h: 32 },
};

export function isTextAboutSourceType(type) {
  return TEXT_ABOUT_SOURCE_TYPES.has(String(type || ''));
}

export function isTextAboutTargetType(type) {
  return TEXT_ABOUT_TARGET_TYPES.has(String(type || ''));
}

export function aboutKindForType(type) {
  if (type === 'pin-card') return 'pin';
  if (type === 'suggest-pin' || type === 'conversation-pin') return 'ai-pin';
  if (type === 'frame') return 'frame';
  return 'shape';
}

export function aabbGap(a, b) {
  if (!a || !b) return Infinity;
  const ax2 = a.x + a.w;
  const ay2 = a.y + a.h;
  const bx2 = b.x + b.w;
  const by2 = b.y + b.h;
  const dx = Math.max(0, a.x - bx2, b.x - ax2);
  const dy = Math.max(0, a.y - by2, b.y - ay2);
  if (dx === 0 && dy === 0) return 0;
  if (dx === 0) return dy;
  if (dy === 0) return dx;
  return Math.hypot(dx, dy);
}

export function aabbOverlapArea(a, b) {
  if (!a || !b) return 0;
  const x = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const y = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  return x * y;
}

function centerDistance(a, b) {
  const dx = (a.x + a.w / 2) - (b.x + b.w / 2);
  const dy = (a.y + a.h / 2) - (b.y + b.h / 2);
  return Math.hypot(dx, dy);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/** Nearest point on the AABB border. Interior points snap to the closest edge. */
export function pointOnAabbBorder(p, box) {
  const x1 = box.x;
  const y1 = box.y;
  const x2 = box.x + box.w;
  const y2 = box.y + box.h;
  const inside = p.x >= x1 && p.x <= x2 && p.y >= y1 && p.y <= y2;
  if (!inside) {
    return {
      x: clamp(p.x, x1, x2),
      y: clamp(p.y, y1, y2),
    };
  }
  const dLeft = p.x - x1;
  const dRight = x2 - p.x;
  const dTop = p.y - y1;
  const dBottom = y2 - p.y;
  const min = Math.min(dLeft, dRight, dTop, dBottom);
  if (min === dLeft) return { x: x1, y: p.y };
  if (min === dRight) return { x: x2, y: p.y };
  if (min === dTop) return { x: p.x, y: y1 };
  return { x: p.x, y: y2 };
}

/** Leader from the middle of the text to the facing edge of the object. */
export function textAboutLeaderPoints(textBounds, targetBounds) {
  const from = {
    x: textBounds.x + textBounds.w / 2,
    y: textBounds.y + textBounds.h / 2,
  };
  return {
    from,
    to: pointOnAabbBorder(from, targetBounds),
  };
}

/**
 * @param {{ id: string, bounds: {x,y,w,h}, explicitAboutId?: string|null }} text
 * @param {Array<{ id: string, bounds: {x,y,w,h}, refId: string, kind?: string }>} objects
 * @returns {{ id: string, refId: string, kind: string, gap: number } | null}
 */
export function findTextAboutObject(text, objects = []) {
  const bounds = text?.bounds;
  if (!bounds || !(bounds.w >= 0) || !(bounds.h >= 0) || !objects.length) return null;

  const scored = [];
  for (const obj of objects) {
    if (!obj?.id || obj.id === text.id || !obj.bounds) continue;
    const explicit = Boolean(
      text.explicitAboutId && obj.refId && String(obj.refId) === String(text.explicitAboutId)
    );
    const gap = aabbGap(bounds, obj.bounds);
    const overlap = aabbOverlapArea(bounds, obj.bounds);
    const maxGap = explicit ? TEXT_ABOUT_EXPLICIT_MAX_GAP : TEXT_ABOUT_MAX_GAP;
    if (gap > maxGap && overlap <= 0) continue;
    scored.push({
      id: String(obj.id),
      refId: String(obj.refId || obj.id),
      kind: obj.kind || 'shape',
      gap,
      overlap,
      center: centerDistance(bounds, obj.bounds),
      explicit,
    });
  }

  if (!scored.length) return null;

  scored.sort((a, b) => {
    if (a.explicit !== b.explicit) return a.explicit ? -1 : 1;
    if (a.gap !== b.gap) return a.gap - b.gap;
    if (a.overlap !== b.overlap) return b.overlap - a.overlap;
    return a.center - b.center;
  });

  const best = scored[0];
  const second = scored[1];
  if (
    !best.explicit
    && second
    && !second.explicit
    && best.gap > 0
    && second.gap <= TEXT_ABOUT_MAX_GAP
    && second.gap / Math.max(best.gap, 1) < TEXT_ABOUT_AMBIGUITY_RATIO
  ) {
    return null;
  }

  return {
    id: best.id,
    refId: best.refId,
    kind: best.kind,
    gap: best.gap,
  };
}

export function fallbackShapeSize(type) {
  return DEFAULT_SIZE[type] || { w: 120, h: 40 };
}

function aboutRefForShape(shape) {
  if (shape?.type === 'pin-card') {
    const activityId = String(shape.meta?.activityId || '').trim();
    if (activityId) return activityId;
  }
  if (shape?.type === 'frame') {
    const boardFrameId = String(shape.meta?.boardFrameId || '').trim();
    if (boardFrameId) return boardFrameId;
    const raw = String(shape.id || '');
    if (raw.startsWith('shape:frame-')) {
      const logical = raw.slice('shape:frame-'.length);
      if (logical) return logical;
    }
  }
  return String(shape?.id || '');
}

function pageBoundsFromEditor(editor, shape) {
  const bounds = editor.getShapePageBounds?.(shape) || editor.getShapePageBounds?.(shape.id);
  if (bounds && Number.isFinite(bounds.w) && Number.isFinite(bounds.h)) {
    return {
      x: bounds.x,
      y: bounds.y,
      w: Math.max(0, bounds.w),
      h: Math.max(0, bounds.h) || fallbackShapeSize(shape.type).h,
    };
  }
  const fallback = fallbackShapeSize(shape.type);
  return {
    x: Number(shape.x) || 0,
    y: Number(shape.y) || 0,
    w: Number(shape.props?.w) || fallback.w,
    h: Number(shape.props?.h) || fallback.h,
  };
}

/**
 * Live canvas links: each text/note → the object it belongs to (if any).
 */
export function collectTextAboutLinks(editor) {
  if (!editor?.getCurrentPageShapes) return [];
  const texts = [];
  const objects = [];
  const frames = [];

  for (const shape of editor.getCurrentPageShapes() || []) {
    if (!shape?.id) continue;
    const bounds = pageBoundsFromEditor(editor, shape);
    if (isTextAboutSourceType(shape.type)) {
      texts.push({
        id: String(shape.id),
        bounds,
        explicitAboutId: String(shape.meta?.activityId || '').trim() || null,
      });
      continue;
    }
    if (!isTextAboutTargetType(shape.type)) continue;
    const entry = {
      id: String(shape.id),
      bounds,
      refId: aboutRefForShape(shape),
      kind: aboutKindForType(shape.type),
    };
    if (shape.type === 'frame') frames.push(entry);
    else objects.push(entry);
  }

  const links = [];
  for (const text of texts) {
    // Prefer a member object over the cluster that contains the text.
    const hit = findTextAboutObject(text, objects)
      || findTextAboutObject(text, frames);
    if (!hit) continue;
    const textShape = editor.getShape?.(text.id);
    const targetShape = editor.getShape?.(hit.id);
    const textBounds = text.bounds;
    const targetBounds = objects.find(o => o.id === hit.id)?.bounds;
    links.push({
      textId: text.id,
      targetId: hit.id,
      aboutId: hit.refId,
      aboutKind: hit.kind,
      gap: hit.gap,
      textBounds,
      targetBounds,
      explicit: Boolean(
        text.explicitAboutId && hit.refId === text.explicitAboutId
      ),
      textType: textShape?.type || 'text',
      targetType: targetShape?.type || '',
    });
  }
  return links;
}

export function findLinkForText(links, textId) {
  const id = String(textId || '');
  return (links || []).find(link => link.textId === id) || null;
}

export function findLinksForTarget(links, targetId) {
  const id = String(targetId || '');
  return (links || []).filter(link => link.targetId === id || link.aboutId === id);
}
